import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import type { SetupStep } from './steps'

export interface RailStep {
  key: SetupStep
  label: string
  done: boolean
  /** Not reachable yet: the competition has to exist first. */
  isDisabled?: boolean
}

/**
 * The setup steps down the left on a wide screen, and a "Step 3 of 7" bar with
 * a scrollable strip on a phone. Every reachable step is clickable: the order
 * is a suggestion, not a gate.
 */
export function StepRail({
  steps,
  current,
  onSelect,
  note,
}: {
  steps: RailStep[]
  current: SetupStep
  onSelect: (step: SetupStep) => void
  note?: string
}) {
  const { t } = useTranslation()
  const index = steps.findIndex((s) => s.key === current)

  return (
    <>
      {/* Phone: the position, a segmented bar, and the steps as a strip. */}
      <div className="flex flex-col gap-2 lg:hidden">
        <p className="text-sm font-semibold">
          {t('competitionSetup.mobileStep', {
            n: index + 1,
            total: steps.length,
            name: steps[index]?.label,
          })}
        </p>
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
          {steps.map((s) => (
            <span
              key={s.key}
              className={`h-1 rounded-full ${
                s.key === current ? 'bg-primary' : s.done ? 'bg-success' : 'bg-default-200'
              }`}
            />
          ))}
        </div>
      </div>

      <nav
        aria-label={t('competitionSetup.stepsLabel')}
        className="flex gap-1 overflow-x-auto scrollbar-hide lg:w-56 lg:shrink-0 lg:flex-col lg:overflow-visible"
      >
        {steps.map((s, i) => {
          const isCurrent = s.key === current
          return (
            <div key={s.key} className="contents">
              {/* The line between "the competition exists" and "fill it in". */}
              {i === 3 && <span className="mx-3 my-1.5 hidden h-px bg-divider lg:block" />}
              <Button
                variant="light"
                isDisabled={s.isDisabled}
                aria-current={isCurrent ? 'step' : undefined}
                onPress={() => onSelect(s.key)}
                className={`h-11 shrink-0 justify-start gap-2.5 px-3 text-sm lg:w-full ${
                  isCurrent ? 'bg-content1 font-semibold shadow-sm' : 'text-foreground/75'
                }`}
                startContent={
                  <span
                    className={`grid size-6 shrink-0 place-items-center rounded-full border-1.5 text-xs font-semibold ${
                      isCurrent
                        ? 'border-primary bg-primary text-primary-foreground'
                        : s.done
                          ? 'border-success bg-success text-success-foreground'
                          : 'border-default-300'
                    }`}
                    aria-hidden="true"
                  >
                    {s.done && !isCurrent ? <CheckIcon /> : i + 1}
                  </span>
                }
              >
                {s.label}
              </Button>
            </div>
          )
        })}
        {note && (
          <p className="mt-3 hidden px-3 text-xs leading-relaxed text-foreground/60 lg:block">
            {note}
          </p>
        )}
      </nav>
    </>
  )
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3.5"
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  )
}
